import { createFileRoute } from '@tanstack/react-router'
import { Fragment, lazy, Suspense, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { motion, useScroll, useTransform, useMotionTemplate, useMotionValue, useSpring, useInView, type HTMLMotionProps } from 'motion/react'
import { type CymaticShape } from '@/components/CymaticInteractive'

import FluidImage from '../components/FluidImage'
import { LineReveal } from '../components/TextReveal'
import { ScrollWipeText } from '@/components/ScrollWipeText'
import { runeTest } from '../resolves/rune-test'

const CymaticInteractive = lazy(() => import('../components/CymaticInteractive'))

// Same randomized Chladni framework as the platform hero: a random mode pair
// is picked on page load for the textless scene above the intro.
const SHAPE_POOL: [number, number][] = [[2, 1], [3, 1], [3, 2], [4, 1], [2, 2]]
function randomShape(): CymaticShape {
  const shuffled = [...SHAPE_POOL].sort(() => Math.random() - 0.5)
  return { modes: [shuffled[0], shuffled[1]] }
}
import { CtaButton } from '@/components/home/CtaButton'
import { EASE_OUT, BODY_FONT, HEADING_FONT, horizontalScrim } from '@/lib/constants'
import { COLORS, EYEBROW, GUTTER, GREEN1_RGB, NEUTRAL8_RGB } from '@/components/home/tokens'
import { useIsMobile } from '@/lib/hooks'
import { useLenis } from '@/providers/lenis-provider'


const paragraphStyle = {
  fontFamily: BODY_FONT,
  fontSize: 'var(--text-4xl)',
  fontWeight: 300,
  lineHeight: 'var(--leading-snug)',
  letterSpacing: '-0.03px',
  margin: 0,
} as const

export const Route = createFileRoute('/company')({
  component: AboutPage,
})

// Recruiting CTA values — a mint lead phrase followed by its body sentence
const VALUES = [
  { lead: 'We build customer-first.', body: "The product comes from problems we've watched real security teams struggle with, not the other way around. Every roadmap conversation starts with something a CISO told us last week." },
  { lead: 'We stay humble.', body: "We hire people who share the mission and trust each other to deliver, and we'd rather see good work than hear about it. Teamwork over showmanship." },
  { lead: 'We hold ourselves accountable.', body: 'The work speaks louder than the person doing it, and we keep politics out of the way of it.' },
  { lead: 'We move with urgency.', body: "The window to build a durable security company in the AI era is open now, and it won't stay open. We move at the pace the threat demands." },
]

const FOUNDERS = [
  {
    name: 'Elias (Lou) Manousos',
    title: 'CEO, CO-FOUNDER',
    linkedin: 'https://www.linkedin.com/in/eliasmanousos/',
    photo: `${import.meta.env.BASE_URL}images/about/founder-lou.webp`,
    bio: 'Elias (Lou) Manousos is a cybersecurity entrepreneur and technology leader known for building and scaling security platforms. He co-founded RiskIQ, which was acquired by Microsoft in 2021, where he went on to serve as Corporate VP over Threat Intelligence and the development of Microsoft Security Copilot.',
    stats: [
      { number: '3', label: 'cybersecurity startups successfully exited' },
      { number: '20+', label: 'years building enterprise cybersecurity platforms' },
    ],
    imageFirst: true,
  },
  {
    name: 'Brandon Dixon',
    title: 'CTO, CO-FOUNDER',
    linkedin: 'https://www.linkedin.com/in/brandonsdixon/',
    photo: `${import.meta.env.BASE_URL}images/about/founder-brandon.webp`,
    bio: 'Brandon Dixon is a security researcher and product leader who turns complex telemetry into actionable intelligence. He co-founded PassiveTotal (acquired by RiskIQ) and served as a Security AI Strategist at Microsoft, where he helped launch Microsoft Security Copilot.',
    stats: [
      { number: '2', label: 'startups successfully exited' },
      { number: '15+', label: 'years of analyst and product building' },
    ],
    imageFirst: false,
  },
]

// `highlight` and `suffix` are optional: not every quote has a pulled-out line
// or a trailing clause. Declaring them here (rather than letting TS infer a
// union from the literals below) is what lets the render read `q.highlight` and
// `q.suffix` uniformly without a cast.
type AdvisorQuote = {
  leadIn: string
  body: string
  highlight?: string
  suffix?: string
}

type Advisor = {
  name: string
  title: string
  photo: string
  linkedin: string
  quote?: AdvisorQuote
}

const DEFAULT_QUOTE: AdvisorQuote = {
  leadIn: 'For the first time in several decades,',
  body: 'we have the opportunity to drive real innovation in enterprise cybersecurity by re-anchoring solutions around truly disruptive technologies like Artificial Intelligence. Ent is a next-generation solution that re-defines how we will manage cyber risk against the current and future threats.',
}

// All advisors for the carousel — alphabetical by first name
const ADVISOR_CAROUSEL: Advisor[] = [
  { name: 'Bharat Shah', title: 'Former CVP, Microsoft Azure Cloud Security', photo: `${import.meta.env.BASE_URL}images/about/advisor-shah.webp`, linkedin: 'https://www.linkedin.com/in/bharatshah1/', quote: { leadIn: '', body: 'I see cybersecurity vendors bolt on layer after layer of security models that aren\'t designed for today\'s AI-powered world. The real perimeter is where work actually happens with diverse human and non-human workforce, including agents. Next-generation endpoint security products need to focus on understanding intent and correlating it with user actions.', highlight: 'Ent seems to have cracked the code on this vision.' } },
  { name: 'Gerhard Eschelbeck', title: 'CSO, Kodiak Robotics. Ex-CISO, Google. Ex-CTO, Sophos', photo: `${import.meta.env.BASE_URL}images/about/advisor-eschelbeck.webp`, linkedin: 'https://www.linkedin.com/in/gerhardeschelbeck/', quote: { leadIn: '', body: 'Security products have a long history of adding AI to existing features. The shift in how the workforce and adversaries alike use AI signals the need for a fundamentally different architecture purpose-built with AI.', highlight: 'Ent is designed to solve the noisy detection and response problem at the endpoint with the right level of behavioral context to investigate the right incidents.', suffix: ' This helps security teams understand where to focus while ensuring AI adoption and innovation continue unhindered.' } },
  { name: 'Jim Routh', title: 'Former CISO, American Express, DTCC, Aetna, CVS, Mass Mutual', photo: `${import.meta.env.BASE_URL}images/about/advisor-routh.webp`, linkedin: 'https://www.linkedin.com/in/jmrouth/', quote: { leadIn: '', body: 'In regulated industries, the majority of security incidents trace back to a human decision at a specific moment, not a sophisticated attack. We\'ve built incident response processes that require humans to initiate steps to investigate and respond after the fact.', highlight: 'Ent is building the ability to understand intent in real time and act before a decision becomes a reportable event.', suffix: ' It has the potential to be a digital immune system for the enterprise.' } },
  { name: 'Joe Sullivan', title: 'Former CISO, Cloudflare, Uber, Meta', photo: `${import.meta.env.BASE_URL}images/about/advisor-sullivan.webp`, linkedin: 'https://www.linkedin.com/in/joesu11ivan/', quote: { leadIn: '', body: 'For many CISOs like me, the hardest security problems were never just the external attackers. They were the trusted insiders with legitimate access doing things that looked completely normal until they didn\'t. Especially when the insiders started deploying agents. The modern security stack tells you what happened after the fact. What we needed was something that understood user intent before the damage was done.', highlight: 'That\'s the problem Ent is solving.' } },
  { name: 'Matt DeVost', title: 'CEO/Co-Founder, OODA', photo: `${import.meta.env.BASE_URL}images/about/advisor-devost.webp`, linkedin: 'https://www.linkedin.com/in/devost/', quote: { leadIn: '', body: 'For the first time in several decades, we have the opportunity to drive real innovation in enterprise cybersecurity by re-anchoring solutions around truly disruptive technologies like Artificial Intelligence.', highlight: 'Ent is a next-generation solution that re-defines how we will manage cyber risk', suffix: ' against the current and future threats.' } },
  { name: 'Rob Joyce', title: 'Former NSA Cyber Director', photo: `${import.meta.env.BASE_URL}images/about/advisor-joyce.webp`, linkedin: 'https://www.linkedin.com/in/rob-joyce-b43445116/', quote: { leadIn: '', body: 'We have seen a shift in adversary tactics from targeting infrastructure to people. They learn behavioral patterns and exploit them in ways that look like normal work. Defending against that requires security that understands behavior the same way an adversary does. Most enterprise tools are not designed that way.', highlight: 'Ent is disrupting endpoint security by layering user behavior intelligence', suffix: ' to get ahead of novel threats.' } },
]


// Props extend motion's own div props rather than React.HTMLAttributes: the two
// disagree on `onAnimationStart` (React's DOM AnimationEvent vs motion's
// AnimationDefinition), so spreading the React type onto a motion.div is a type
// error. HTMLMotionProps is the same surface with motion's handler signatures.
// `children` and `style` are deliberately not redeclared — HTMLMotionProps
// already supplies both, and re-declaring `style` as React.CSSProperties would
// intersect down to CSSProperties & MotionStyle, blocking callers from passing
// a MotionValue in style. `offset` is the only prop that's genuinely ours.
function ParallaxText({ children, offset = 30, style, ...props }: { offset?: number } & HTMLMotionProps<'div'>) {
  const ref = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  })
  const isMobile = window.innerWidth < 768
  const y = useTransform(scrollYProgress, [0, 1], isMobile ? [0, 0] : [offset, -offset])

  return (
    <motion.div ref={ref} style={{ ...style, y }} {...props}>
      {children}
    </motion.div>
  )
}


// Scroll-driven copy fade: a block fades up from dim as it enters the viewport
// and back to dim as it leaves — re-animates on every pass (not once).
function ScrollFadeBlock({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  const ref = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start 0.9', 'end 0.15'],
  })
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0.15, 1, 1, 0.15])
  return (
    <motion.div ref={ref} style={{ ...style, opacity }}>
      {children}
    </motion.div>
  )
}


function ClipReveal({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { margin: '-10% 0px', once: true })

  return (
    <motion.div
      ref={ref}
      initial={{ clipPath: 'inset(100% 0% 0% 0%)' }}
      animate={inView ? { clipPath: 'inset(0% 0% 0% 0%)' } : { clipPath: 'inset(100% 0% 0% 0%)' }}
      transition={{ duration: 0.8, delay, ease: EASE_OUT }}
    >
      {children}
    </motion.div>
  )
}



const INVESTORS = [
  { name: 'Craft Ventures', logo: `${import.meta.env.BASE_URL}images/investors/craft.svg`, height: '28px', href: 'https://www.craftventures.com' },
  { name: 'Decibel', logo: `${import.meta.env.BASE_URL}images/investors/decibel.svg`, height: '35px', href: 'https://www.decibel.vc' },
  { name: 'Sequoia', logo: `${import.meta.env.BASE_URL}images/investors/sequoia.svg`, height: '35px', href: 'https://www.sequoiacap.com' },
  { name: 'Shield Capital', logo: `${import.meta.env.BASE_URL}images/about/shield-capital.webp`, height: '58px', href: 'https://www.shieldcap.com' },
  { name: 'Crosspoint Capital', logo: `${import.meta.env.BASE_URL}images/investors/crosspoint.svg`, height: '48px', href: 'https://www.crosspointcapital.com' },
  { name: 'Felicis', logo: `${import.meta.env.BASE_URL}images/investors/felicis.svg`, height: '44px', href: 'https://www.felicis.com' },
  { name: 'IQT', logo: `${import.meta.env.BASE_URL}images/investors/iqt.svg`, height: '54px', href: 'https://www.iqt.org' },
]

function InvestorsSection({ isMobile }: { isMobile: boolean }) {
  const sectionRef = useRef<HTMLDivElement>(null)
  const isInView = useInView(sectionRef, { amount: 0.35 })

  // Notify root layout to change nav/footer colors when green fills the screen
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('investor-overlay', { detail: { active: isInView } }))
  }, [isInView])

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  })

  // 0 = section entering bottom, 0.5 = section centered, 1 = section exited top
  // Symmetric ranges so entry and exit feel the same speed
  const clipInset = useTransform(scrollYProgress, [0, 0.35, 0.65, 1], [50, 0, 0, 50])
  const clipRadius = useTransform(scrollYProgress, [0, 0.35, 0.65, 1], [12, 0, 0, 12])
  const clipPath = useMotionTemplate`inset(${clipInset}% round ${clipRadius}px)`

  // Ease-out for logo animations
  const easeOut = [0.33, 1, 0.68, 1] as const

  return (
    <section
      ref={sectionRef as React.MutableRefObject<HTMLDivElement | null>}
      style={{
        minHeight: isMobile ? 'auto' : '110vh',
        position: 'relative',
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        ...(isMobile ? { backgroundColor: '#8ce4af', padding: '80px 24px' } : {}),
      }}
    >
      {/* Green background — expands from invisible point to fill viewport, driven by scroll */}
      <motion.div
        style={{
          ...(isMobile ? {} : { clipPath }),
          position: isMobile ? 'relative' : 'absolute',
          inset: isMobile ? undefined : 0,
          backgroundColor: isMobile ? 'transparent' : '#8ce4af',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          willChange: isMobile ? 'auto' : 'clip-path',
        }}
      >
        {/* Logos — fixed to viewport center on desktop, static on mobile */}
        <div
          style={{
            position: isMobile ? 'relative' : 'fixed',
            top: isMobile ? undefined : 0,
            left: isMobile ? undefined : 0,
            right: isMobile ? undefined : 0,
            height: isMobile ? 'auto' : '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: isMobile ? 'auto' : 'none',
          }}
        >
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6, delay: isInView ? 0.1 : 0, ease: easeOut }}
            style={{
              ...EYEBROW,
              color: '#07361C',
              margin: '0 0 6vh',
            }}
          >
            Backed by
          </motion.p>
          <div
            style={{
              // Flex-wrap (not grid) so an incomplete last row stays centered.
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'center',
              gap: isMobile ? '10vh 8vw' : '14vh 5vw',
              width: '100%',
              maxWidth: isMobile ? '85vw' : '88vw',
              padding: isMobile ? '0 24px' : '0',
              boxSizing: 'border-box',
            }}
          >
          {INVESTORS.map(({ name, logo, height, href }, i) => (
            <motion.div
              key={name}
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={isInView ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 20, scale: 0.95 }}
              transition={{ duration: 0.7, delay: isInView ? 0.2 + i * 0.08 : 0, ease: easeOut }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '80px',
                // Fixed basis → 2 per row on mobile, 4 on desktop (matching the
                // gap), so full rows fill the width and a short last row centers.
                flex: '0 0 auto',
                flexBasis: isMobile ? 'calc((100% - 8vw) / 2)' : 'calc((100% - 3 * 5vw) / 4)',
              }}
            >
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={name}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  // Re-enable clicks: the parent container sets pointerEvents:none
                  // on desktop so it doesn't block scrolling over the pinned logos.
                  pointerEvents: 'auto',
                }}
              >
                <img
                  src={logo}
                  alt={name}
                  style={{
                    height: isMobile ? `calc(${height} * 1.4)` : height,
                    width: 'auto',
                    objectFit: 'contain',
                    filter: 'brightness(0)',
                  }}
                />
              </a>
            </motion.div>
          ))}
          </div>
        </div>
      </motion.div>
    </section>
  )
}

const CAROUSEL_DURATION = 12000

function AdvisorCarousel({ isMobile }: { isMobile: boolean }) {
  const [active, setActive] = useState(0)
  const activeRef = useRef(0)
  // eslint-disable-next-line react-hooks/purity -- seeding the carousel clock from the wall clock. The value is only ever read inside the RAF loop below and is immediately overwritten on the first tick; a render-pure seed would have no meaning for a time-based animation.
  const startTimeRef = useRef(performance.now())
  const rafRef = useRef<number>(0)
  const progressRefs = useRef<(HTMLDivElement | null)[]>([])

  // Single RAF loop — never torn down, uses refs for current state
  useEffect(() => {
    const tick = (now: number) => {
      const elapsed = now - startTimeRef.current
      const p = Math.min(elapsed / CAROUSEL_DURATION, 1)
      const bar = progressRefs.current[activeRef.current]
      if (bar) bar.style.width = `${p * 100}%`
      if (p >= 1) {
        const next = (activeRef.current + 1) % ADVISOR_CAROUSEL.length
        activeRef.current = next
        startTimeRef.current = now
        setActive(next)
      }
      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafRef.current)
  }, [])

  const goTo = (idx: number) => {
    // Reset all progress bars
    progressRefs.current.forEach((bar) => { if (bar) bar.style.width = '0%' })
    activeRef.current = idx
    // eslint-disable-next-line react-hooks/purity -- restarting the carousel clock from a click handler, not from render. Event handlers are allowed to read the wall clock; the rule does not distinguish them here.
    startTimeRef.current = performance.now()
    setActive(idx)
  }

  // Swipe detection for mobile
  const touchStartX = useRef(0)
  const touchStartY = useRef(0)
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
    touchStartY.current = e.touches[0].clientY
  }
  const handleTouchEnd = (e: React.TouchEvent) => {
    const dx = e.changedTouches[0].clientX - touchStartX.current
    const dy = e.changedTouches[0].clientY - touchStartY.current
    // Only trigger if horizontal swipe is dominant and > 50px
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx < 0) {
        goTo((activeRef.current + 1) % ADVISOR_CAROUSEL.length)
      } else {
        goTo((activeRef.current - 1 + ADVISOR_CAROUSEL.length) % ADVISOR_CAROUSEL.length)
      }
    }
  }

  const sectionRef = useRef<HTMLDivElement>(null)
  const inView = useInView(sectionRef, { margin: '-40% 0px' })

  return (
    <div ref={sectionRef} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} style={{ width: '100%', maxWidth: isMobile ? undefined : '900px', margin: '0 auto', display: 'flex', flexDirection: 'column', justifyContent: isMobile ? 'flex-start' : 'center', height: '100%' }}>
      <p style={{
        ...EYEBROW,
        flexShrink: 0,
        color: '#C1BAAF',
        margin: isMobile ? '0 0 20px' : '0 0 40px',
      }}>
        Advised by
      </p>
      {/* Quote + attribution */}
      <div style={{ position: 'relative', flex: 1, display: 'flex', alignItems: isMobile ? 'flex-start' : 'center' }}>
        {ADVISOR_CAROUSEL.map((a, i) => {
          const q = a.quote ?? DEFAULT_QUOTE
          return (
            <motion.div
              key={a.name}
              animate={{ opacity: i === active ? 1 : 0 }}
              transition={{ duration: 0.6, ease: EASE_OUT }}
              style={{
                position: i === 0 ? 'relative' : 'absolute',
                top: i === 0 ? undefined : 0,
                left: 0,
                right: 0,
                bottom: i === 0 ? undefined : 0,
                display: i === 0 ? 'flex' : undefined,
                alignItems: i === 0 ? 'center' : undefined,
                pointerEvents: i === active ? 'auto' : 'none',
              }}
            >
              <div style={{ width: '100%' }}>
                {/* Quote text */}
                <p style={{
                  fontFamily: BODY_FONT,
                  fontSize: isMobile ? 'var(--text-base)' : 'var(--text-lg)',
                  fontWeight: 300,
                  lineHeight: 'var(--leading-normal)',
                  letterSpacing: '-0.01px',
                  color: '#d1cbc1',
                  margin: 0,
                  textAlign: 'left',
                  maxWidth: undefined,
                }}>
                  {q.leadIn && <><span style={{ color: COLORS.green1, fontFamily: HEADING_FONT, fontWeight: 200, fontSize: isMobile ? 'var(--text-lg)' : 'var(--text-xl)' }}>{q.leadIn}</span>{' '}</>}
                  {q.body}
                  {q.highlight && <>{' '}<span style={{ color: COLORS.green1, fontFamily: HEADING_FONT, fontWeight: 200, fontSize: isMobile ? 'var(--text-lg)' : 'var(--text-xl)' }}>{q.highlight}</span></>}
                  {q.suffix && q.suffix}
                </p>

                {/* Divider */}
                <div style={{ height: '1px', backgroundColor: COLORS.neutral6, margin: isMobile ? '12px 0' : 'clamp(16px, 2vw, 32px) 0' }} />

                {/* Advisor info — stacked on mobile, row on desktop */}
                {isMobile ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <a href={a.linkedin} target="_blank" rel="noopener noreferrer" style={{ flexShrink: 0 }}>
                      <img
                        src={a.photo}
                        alt={a.name}
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '4px',
                          objectFit: 'cover',
                          display: 'block',
                        }}
                      />
                    </a>
                    <div>
                      <a
                        href={a.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          fontFamily: HEADING_FONT,
                          fontSize: 'var(--text-base)',
                          fontWeight: 200,
                          lineHeight: 'var(--leading-normal)',
                          color: '#99f2c1',
                          textDecoration: 'none',
                        }}
                      >
                        {a.name}
                      </a>
                      <p style={{
                        fontFamily: BODY_FONT,
                        fontSize: 'var(--text-sm)',
                        fontWeight: 300,
                        lineHeight: 'var(--leading-normal)',
                        color: COLORS.neutral1,
                        margin: 0,
                        marginTop: '2px',
                      }}>
                        {a.title}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(10px, 1vw, 16px)' }}>
                    <a href={a.linkedin} target="_blank" rel="noopener noreferrer" style={{ flexShrink: 0 }}>
                      <img
                        src={a.photo}
                        alt={a.name}
                        style={{
                          width: 'clamp(48px, 5vw, 72px)',
                          height: 'clamp(48px, 5vw, 72px)',
                          borderRadius: '4px',
                          objectFit: 'cover',
                          display: 'block',
                        }}
                      />
                    </a>
                    <div>
                      <a
                        href={a.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          fontFamily: HEADING_FONT,
                          fontSize: 'var(--text-lg)',
                          fontWeight: 200,
                          lineHeight: 'var(--leading-normal)',
                          color: '#99f2c1',
                          textDecoration: 'none',
                          textUnderlineOffset: '4px',
                          textDecorationColor: 'transparent',
                          transition: 'text-decoration-color 0.3s ease-out',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; e.currentTarget.style.textDecorationColor = '#99f2c1' }}
                        onMouseLeave={(e) => { e.currentTarget.style.textDecorationColor = 'transparent' }}
                      >
                        {a.name}
                      </a>
                      <p style={{
                        fontFamily: BODY_FONT,
                        fontSize: 'var(--text-base)',
                        fontWeight: 300,
                        lineHeight: 'var(--leading-normal)',
                        color: COLORS.neutral1,
                        margin: 0,
                        marginTop: '2px',
                      }}>
                        {a.title}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )
        })}
      </div>

      {/* Progress navigation — portaled into footer center slot */}
      {(() => {
        const slot = document.getElementById('footer-center-slot')
        if (!slot) return null
        return createPortal(
          <div style={{
            display: 'flex',
            alignItems: 'center',
            opacity: inView ? 1 : 0,
            pointerEvents: inView ? 'auto' : 'none',
            transition: 'opacity 0.4s ease-out',
          }}>
            {ADVISOR_CAROUSEL.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                aria-label={`View ${ADVISOR_CAROUSEL[i].name}`}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '16px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  touchAction: 'manipulation',
                }}
              >
                <div
                  style={{
                    width: i === active ? '40px' : '6px',
                    height: i === active ? '3px' : '6px',
                    borderRadius: i === active ? '2px' : '3px',
                    backgroundColor: `rgba(${GREEN1_RGB}, 0.25)`,
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'width 0.4s cubic-bezier(0.33,1,0.68,1), height 0.4s cubic-bezier(0.33,1,0.68,1), border-radius 0.4s cubic-bezier(0.33,1,0.68,1)',
                  }}
                >
                  <div
                    ref={(el) => { progressRefs.current[i] = el }}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      bottom: 0,
                      width: '0%',
                      backgroundColor: COLORS.green1,
                      borderRadius: '2px',
                      display: i === active ? 'block' : 'none',
                    }}
                  />
                </div>
              </button>
            ))}
          </div>,
          slot
        )
      })()}
    </div>
  )
}

function useIsNarrow(breakpoint = 1100) {
  const [isNarrow, setIsNarrow] = useState(false)
  useEffect(() => {
    const check = () => setIsNarrow(window.innerWidth < breakpoint)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [breakpoint])
  return isNarrow
}

const mobileSnapSection: React.CSSProperties = {
}

function MobileAboutPage() {

  return (
    <div style={{
      position: 'relative',
      background: COLORS.neutral8,
      overflowX: 'hidden',
      overflowY: 'auto',
      height: '100dvh',
      WebkitOverflowScrolling: 'touch',
    }}>
      {/* Hero */}
      <section style={{ ...mobileSnapSection, minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: `80px ${GUTTER}`, boxSizing: 'border-box' }}>
        <h1 style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}>
          About Ent — Our Team, Investors &amp; Advisors
        </h1>
        <p style={{ fontFamily: BODY_FONT, fontSize: 'var(--text-xl)', fontWeight: 300, lineHeight: 'var(--leading-snug)', letterSpacing: '-0.01px', color: COLORS.neutral2, maxWidth: '600px', textAlign: 'center', margin: 0 }}>
          Most security tools tell you what already happened.{' '}
          <span style={{ color: COLORS.green1, fontFamily: HEADING_FONT, fontWeight: 200, fontSize: 'var(--text-2xl)' }}>Ent protects work as it happens,</span>
          {' '}reading what people and AI agents are actually trying to do, and stepping in at the moment it matters.
        </p>
      </section>

      {/* Founders */}
      {FOUNDERS.map((founder, fi) => (
        <section key={fi} style={{ ...mobileSnapSection, minHeight: '100dvh', padding: `60px ${GUTTER}`, paddingBottom: fi === FOUNDERS.length - 1 ? '120px' : '60px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
          <img
            src={founder.photo}
            alt={founder.name}
            style={{ width: '70%', maxWidth: '300px', aspectRatio: '400 / 530', objectFit: 'cover', borderRadius: '4px', marginBottom: '24px' }}
          />
          <a href={founder.linkedin} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
            <span style={{ fontFamily: HEADING_FONT, fontSize: 'var(--text-4xl)', fontWeight: 200, lineHeight: 'var(--leading-tight)', color: COLORS.green1 }}>{founder.name}</span>
          </a>
          <span style={{ fontFamily: BODY_FONT, fontSize: 'var(--text-sm)', fontWeight: 400, lineHeight: 'var(--leading-snug)', letterSpacing: '-0.28px', color: COLORS.green1 }}>{founder.title}</span>
          <p style={{ fontFamily: BODY_FONT, fontSize: 'var(--text-base)', fontWeight: 400, lineHeight: 'var(--leading-normal)', color: COLORS.neutral2, marginTop: '24px', marginBottom: '32px', maxWidth: '500px' }}>{founder.bio}</p>
          {founder.stats?.map((stat, si) => (
            <div key={si} style={{ borderTop: '1px solid #9A9389', paddingTop: '14px', marginBottom: '12px', display: 'flex', gap: '16px', alignItems: 'center', width: '100%', maxWidth: '400px' }}>
              <span style={{ fontFamily: BODY_FONT, fontSize: 'var(--text-2xl)', fontWeight: 400, lineHeight: 'var(--leading-tight)', color: COLORS.green1, width: '70px', flexShrink: 0, textAlign: 'center' }}>{stat.number}</span>
              <span style={{ fontFamily: BODY_FONT, fontSize: 'var(--text-base)', fontWeight: 600, lineHeight: 'var(--leading-normal)', color: COLORS.neutral2 }}>{stat.label}</span>
            </div>
          ))}
        </section>
      ))}

      {/* Investors */}
      <section style={{ ...mobileSnapSection, minHeight: '100dvh', padding: `80px ${GUTTER}`, boxSizing: 'border-box', backgroundColor: '#8ce4af', display: 'grid', gridTemplateColumns: '1fr 1fr', alignContent: 'center', alignItems: 'center', justifyItems: 'center', gap: '7vh 8vw' }}>
        <p style={{ ...EYEBROW, gridColumn: '1 / -1', color: '#07361C', margin: '0 0 3vh', textAlign: 'center' }}>Backed by</p>
        {INVESTORS.map(({ name, logo, height }) => (
          <img key={name} src={logo} alt={name} style={{ height: `calc(${height} * 0.7)`, width: 'auto', maxWidth: '100%', objectFit: 'contain', filter: 'brightness(0)' }} />
        ))}
      </section>

      {/* Advisors carousel */}
      <section style={{ ...mobileSnapSection, minHeight: '100dvh', padding: `30px ${GUTTER} 60px`, boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <AdvisorCarousel isMobile />
      </section>

      {/* Our team */}
      <section style={{ ...mobileSnapSection, minHeight: '100dvh', padding: `60px ${GUTTER}`, boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ fontFamily: BODY_FONT, fontSize: 'var(--text-xl)', fontWeight: 300, lineHeight: 'var(--leading-snug)', letterSpacing: '-0.01px', color: COLORS.neutral2, maxWidth: '600px', textAlign: 'center', margin: 0 }}>
          Ent brings together{' '}
          <span style={{ color: COLORS.green1, fontFamily: HEADING_FONT, fontWeight: 200, fontSize: 'var(--text-2xl)' }}>world-class threat and AI researchers, security practitioners, and product builders </span>
          who have shipped this kind of work before. The team carries scale-and-speed experience from{' '}
          <span style={{ color: COLORS.green1, fontFamily: HEADING_FONT, fontWeight: 200, fontSize: 'var(--text-2xl)' }}>Microsoft, Amazon, Google Mandiant, CrowdStrike, and Palo Alto.</span>
        </p>
      </section>

      {/* Join us */}
      <section style={{ ...mobileSnapSection, minHeight: '100dvh', padding: `80px ${GUTTER}`, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', backgroundColor: '#07361c' }}>
        <img
          src={`${import.meta.env.BASE_URL}images/rune.svg`}
          alt=""
          style={{ height: '24vh', width: 'auto', marginBottom: '56px', opacity: 0.8, marginLeft: '15%' }}
        />
        {VALUES.map((value, i) => (
          <p key={i} style={{ ...paragraphStyle, fontSize: 'var(--text-base)', lineHeight: 'var(--leading-normal)', color: COLORS.neutral2, textAlign: 'left', maxWidth: '500px', marginBottom: i < VALUES.length - 1 ? '20px' : 0 }}>
            <span style={{ color: COLORS.green1 }}>{value.lead}</span>{' '}{value.body}
          </p>
        ))}
        <div style={{ marginTop: 32 }}>
          <CtaButton label="Join our team" variant="solid" icon="arrow" iconPosition="right" to="/joinus/" />
        </div>
      </section>
    </div>
  )
}

function AboutPage() {
  const isMobile = useIsMobile()
  return isMobile ? <MobileAboutPage /> : <DesktopAboutPage />
}

function DesktopAboutPage() {
  const isMobile = false
  const isNarrow = useIsNarrow()
  const lenis = useLenis()

  // Closing-section rune backdrop: FIXED behind the page (like the homepage
  // demo/footer rune) — the content scrolls over it; it fades in as the
  // closing section arrives and persists behind the (transparent) footer.
  const closingRef = useRef<HTMLElement | null>(null)
  const { scrollYProgress: runeIn } = useScroll({
    target: closingRef,
    offset: ['start end', 'start 0.5'],
  })
  // The rune layer itself is always on; an opaque page-bg cover above it
  // fades OUT across the gap between the team statement and the closing
  // section, revealing the rune beneath (homepage backdrop pattern).
  const runeCover = useTransform(runeIn, (v) => 1 - v)

  // Textless cymatic scene above the intro (platform-hero template): a random
  // figure forms softly on load; static (not scroll-driven), reduced-motion aware.
  const [cymaticShape] = useState(randomShape)
  const cymaticScroll = useMotionValue(1)
  const [reducedMotion, setReducedMotion] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReducedMotion(mq.matches)
    const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  // Slower, heavier scroll on about page (desktop only)
  useEffect(() => {
    if (!lenis || isMobile) return
    const origLerp = lenis.options.lerp
    const origWheel = lenis.options.wheelMultiplier
    // eslint-disable-next-line react-hooks/immutability -- `lenis` is a third-party instance, not React state. Retuning its options is the library's supported way to change scroll feel per route, and the cleanup below restores the originals.
    lenis.options.lerp = 0.055
    lenis.options.wheelMultiplier = 0.7
    return () => {
      lenis.options.lerp = origLerp
      lenis.options.wheelMultiplier = origWheel
    }
  }, [lenis, isMobile])



  const { scrollYProgress } = useScroll()
  const springProgress = useSpring(scrollYProgress, {
    stiffness: 60,
    damping: 20,
    mass: 1.2,
    restDelta: 0.0005,
  })
  // Skip spring on mobile — continuous physics sim causes scroll jank
  const smoothProgress = isMobile ? scrollYProgress : springProgress

  // Right glow: fades in after hero, fades out as second bio arrives
  const rightGlowIn = useTransform(smoothProgress, [0.05, 0.15], [0, 1])
  const rightGlowOut = useTransform(smoothProgress, [0.25, 0.35], [1, 0])
  const rightGlowOpacity = useTransform(
    [rightGlowIn, rightGlowOut],
    ([inp, out]: number[]) => inp * out
  )

  // Left glow: fades in as second bio arrives, fades out near bottom
  const leftGlowIn = useTransform(smoothProgress, [0.25, 0.35], [0, 1])
  const leftGlowOut = useTransform(smoothProgress, [0.55, 0.65], [1, 0])
  const leftGlowOpacity = useTransform(
    [leftGlowIn, leftGlowOut],
    ([inp, out]: number[]) => inp * out
  )

  // No background on this root: the root layout's fixed page-color layer (z0)
  // paints neutral8 (#1F1D17), keeping content transparent so the body-portaled closing
  // rune (z1) shows through — same architecture as home/platform.
  return (
    <div style={{ position: 'relative', overflowX: 'clip' }}>

      {/* Green glows — portaled to body so they're not clipped by overflow:hidden */}
      {createPortal(
        <>
          <motion.img
            alt=""
            className="glow-drift-right"
            src={`${import.meta.env.BASE_URL}images/green_glow.svg`}
            style={{
              position: 'fixed',
              width: '1683px',
              height: '1683px',
              right: '-800px',
              top: '-800px',
              pointerEvents: 'none',
              zIndex: 1,
              opacity: rightGlowOpacity,
            }}
          />
          <motion.img
            alt=""
            className="glow-drift-left"
            src={`${import.meta.env.BASE_URL}images/green_glow.svg`}
            style={{
              position: 'fixed',
              width: '1683px',
              height: '1683px',
              left: '-800px',
              bottom: '-800px',
              pointerEvents: 'none',
              zIndex: 1,
              opacity: leftGlowOpacity,
            }}
          />
        </>,
        document.body
      )}


      {/* ─── Hero: cymatic scene with the intro statement overlaid. The cymatic
          + gradient overlays sit behind; the paragraph is static (no scroll
          interaction) and anchored left over the horizontal scrim. ─── */}
      <section
        style={{
          height: 'calc(92 * var(--vh))',
          position: 'relative',
          overflow: 'hidden',
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          padding: `0 ${GUTTER}`,
          boxSizing: 'border-box',
        }}
      >
        {/* Semantic h1 for SEO — visually hidden, screen-reader accessible */}
        <h1 style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}>
          About Ent — Our Team, Investors &amp; Advisors
        </h1>

        {!reducedMotion && (
          <Suspense fallback={null}>
            <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
              <CymaticInteractive
                scrollProgress={cymaticScroll}
                shape={cymaticShape}
                resolveAnimation={{ from: 0, to: 0.5, durationMs: 1400 }}
                particleColor="#564F44"
              />
            </div>
          </Suspense>
        )}
        {/* Vertical gradient overlay — same as the platform hero */}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background: `linear-gradient(180deg, rgba(7,54,28,0) 0%, rgba(${NEUTRAL8_RGB},0.5) 68%, rgba(${NEUTRAL8_RGB},1) 100%)`,
          }}
        />
        {/* Same dark overlay, horizontal — solid left fading to transparent right.
            Anchors the left-aligned copy over the cymatic. */}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background: horizontalScrim(NEUTRAL8_RGB),
          }}
        />

        {/* Intro statement — static (scroll-wipe / parallax removed), mint
            highlight on the payoff phrase. */}
        <p
          style={{
            position: 'relative',
            zIndex: 1,
            fontFamily: HEADING_FONT,
            fontWeight: 200,
            fontSize: 'var(--text-4xl)',
            lineHeight: 1.2,
            letterSpacing: '-0.02em',
            color: COLORS.neutral2,
            maxWidth: '960px',
            textAlign: 'left',
            margin: 0,
          }}
        >
          Most security tools tell you what already happened.{' '}
          <span style={{ color: COLORS.green1 }}>Ent protects work as it happens,</span>{' '}
          reading what people and AI agents are actually trying to do,{' '}
          <span style={{ color: COLORS.green1 }}>and stepping in at the moment it matters.</span>
        </p>
      </section>

      {/* ─── Section 2: Meet the founders ─── */}
      {FOUNDERS.map((founder, fi) => {
        const photo = (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-10% 0px' }}
            transition={{ duration: 1, ease: EASE_OUT }}
          >
            <FluidImage
              src={founder.photo}
              alt={founder.name}
              disableParallax
              width={isMobile ? '80%' : '400px'}
              height={isMobile ? 'auto' : 'auto'}
              style={{
                minWidth: isMobile ? undefined : '280px',
                aspectRatio: '400 / 530',
                flexShrink: 1,
              }}
            />
          </motion.div>
        )

        const bio = (
          <ParallaxText offset={60} style={{ flex: 1, maxWidth: isNarrow ? '100%' : '560px' }}>
            <div style={{ marginBottom: '32px' }}>
              <LineReveal staggerAmount={0}
                text={founder.name}
                style={{
                  fontFamily: HEADING_FONT,
                  fontSize: 'var(--text-4xl)',
                  fontWeight: 200,
                  lineHeight: 'var(--leading-tight)',
                  color: COLORS.green1,
                  margin: 0,
                }}
                delay={0.2}
              />
              <LineReveal staggerAmount={0}
                text={founder.title}
                style={{
                  fontFamily: BODY_FONT,
                  fontSize: 'var(--text-sm)',
                  fontWeight: 400,
                  lineHeight: 'var(--leading-snug)',
                  letterSpacing: '-0.28px',
                  color: COLORS.green1,
                  margin: 0,
                }}
                delay={0.4}
              />
              <ClipReveal delay={0.5}>
                <a
                  href={founder.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  // The link's only content is a decorative SVG, so without an
                  // explicit name a screen reader announces "link" and nothing
                  // else. The advisor links below don't need this — they wrap an
                  // <img alt={name}>, which already supplies the accessible name.
                  aria-label={`${founder.name} on LinkedIn`}
                  style={{ display: 'inline-block', marginTop: '8px', pointerEvents: 'auto' }}
                >
                  <svg aria-hidden="true" focusable="false" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" fill={COLORS.green1}/>
                  </svg>
                </a>
              </ClipReveal>
            </div>
            <LineReveal staggerAmount={0}
              text={founder.bio}
              style={{
                fontFamily: BODY_FONT,
                fontSize: 'var(--text-base)',
                fontWeight: 400,
                lineHeight: 'var(--leading-normal)',
                color: COLORS.neutral2,
                marginTop: 0,
                marginBottom: '40px',
              }}
              delay={0.5}
            />

            {founder.stats?.map((stat, i) => (
              <motion.div
                key={i}
                initial={{ clipPath: 'inset(100% 0% 0% 0%)' }}
                whileInView={{ clipPath: 'inset(0% 0% 0% 0%)' }}
                viewport={{ once: true, margin: '-10% 0px 200% 0px' }}
                transition={{ duration: 0.8, delay: 0.5, ease: EASE_OUT }}
              >
                <div
                  style={{
                    borderTop: '1px solid #9A9389',
                    paddingTop: '14px',
                    marginBottom: i < (founder.stats?.length ?? 0) - 1 ? '12px' : 0,
                    display: 'flex',
                    gap: '20px',
                    alignItems: 'center',
                  }}
                >
                  <span
                    style={{
                      fontFamily: BODY_FONT,
                      fontSize: 'var(--text-4xl)',
                      fontWeight: 400,
                      lineHeight: 'var(--leading-tight)',
                      letterSpacing: '-0.01em',
                      color: COLORS.green1,
                      width: '90px',
                      flexShrink: 0,
                    }}
                  >
                    {stat.number}
                  </span>
                  <span
                    style={{
                      fontFamily: BODY_FONT,
                      fontSize: 'var(--text-base)',
                      fontWeight: 600,
                      lineHeight: 'var(--leading-normal)',
                      color: COLORS.neutral2,
                    }}
                  >
                    {stat.label}
                  </span>
                </div>
              </motion.div>
            ))}
          </ParallaxText>
        )

        return (
          <Fragment key={fi}>
            <section
            style={{
              minHeight: '100vh',
              position: 'relative',
              zIndex: 2,
              display: 'flex',
              alignItems: isMobile ? 'flex-start' : 'center',
              justifyContent: 'center',
              padding: `120px ${GUTTER}`,
              boxSizing: 'border-box',
              ...(isMobile ? {} : { contentVisibility: 'auto' as const, containIntrinsicSize: 'auto 100vh' }),
                }}
          >

            <div style={{ width: '100%' }}>
              <div
                style={{
                  display: 'flex',
                  gap: isNarrow ? '40px' : '120px',
                  alignItems: 'flex-start',
                  flexDirection: isNarrow ? 'column' : (founder.imageFirst ? 'row' : 'row-reverse'),
                }}
              >
                {photo}
                {bio}
              </div>

            </div>
    
          </section>
        </Fragment>
        )
      })}

      {/* ─── Section 3: Investors ─── */}
      <InvestorsSection isMobile={isMobile} />

      {/* ─── Section 4: Advisors quote ─── */}
      <section
        style={{
          minHeight: '100vh',
          position: 'relative',
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          padding: `80px ${GUTTER}`,
          boxSizing: 'border-box',
        }}
      >
        <AdvisorCarousel isMobile={isMobile} />
      </section>


      {/* ─── Section 5: Team statement ─── */}
      <section
        style={{
          minHeight: '100vh',
          position: 'relative',
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          padding: `0 ${GUTTER}`,
          boxSizing: 'border-box',
          ...(isMobile ? {} : { contentVisibility: 'auto' as const, containIntrinsicSize: 'auto 100vh' }),
          marginTop: '-10vh',
        }}
      >
<ParallaxText offset={50} style={{ width: '100%', display: 'flex', justifyContent: 'flex-start' }}>
          {/* Same scroll-wipe statement treatment as the intro paragraph */}
          <ScrollWipeText
            text="Ent brings together world-class threat and AI researchers, security practitioners, and product builders who have shipped this kind of work before. The team carries scale-and-speed experience from Microsoft, Amazon, Google Mandiant, CrowdStrike, and Palo Alto."
            as="p"
            highlights={[
              'world-class threat and AI researchers, security practitioners, and product builders',
              'Microsoft, Amazon, Google Mandiant, CrowdStrike, and Palo Alto.',
            ]}
            style={{
              fontFamily: HEADING_FONT,
              fontWeight: 200,
              fontSize: 'var(--text-4xl)',
              lineHeight: 1.2,
              letterSpacing: '-0.02em',
              color: '#F8F6F2',
              maxWidth: '960px',
              textAlign: 'left',
              margin: 0,
            }}
          />
        </ParallaxText>

      </section>

      {/* ─── Section 6: Bottom closing quote ─── */}
      <section
        ref={closingRef}
        style={{
          minHeight: '100vh',
          position: 'relative',
          zIndex: 2,
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'center',
          justifyContent: 'center',
          padding: `100px ${GUTTER} 160px`,
          boxSizing: 'border-box',
          gap: isMobile ? 0 : '0',
          overflow: 'visible',
        }}
      >
        {/* Cymatic rune backdrop — FIXED full-viewport layer portaled to the
            body (homepage pattern): it doesn't scroll; the section copy and
            the footer move over it. Fades in as this section approaches.
            Particle color matches the scene at the top of this page; the
            glyph sits right-of-center, composing with the left-aligned text. */}
        {!isMobile && !reducedMotion && typeof document !== 'undefined' && createPortal(
          <Suspense fallback={null}>
            <div style={{ position: 'fixed', inset: 0, zIndex: 1, pointerEvents: 'none' }}>
              <CymaticInteractive
                scrollProgress={cymaticScroll}
                {...runeTest}
                bgColor={undefined}
                particleColor="#564F44"
                cursorRadius={0.16}
                cursorForce={0.0008}
              />
              {/* Horizontal wash — solid left fading right, same as the scene
                  at the top of the page; anchors the left-aligned copy */}
              <div
                aria-hidden
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: horizontalScrim(NEUTRAL8_RGB),
                }}
              />
              {/* Opaque page-bg cover — dissolves between the team statement
                  and the closing section so the rune emerges beneath */}
              <motion.div style={{ position: 'absolute', inset: 0, background: COLORS.neutral8, opacity: runeCover }} />
            </div>
          </Suspense>,
          document.body,
        )}

        {!isMobile && (
          <ParallaxText offset={50} style={{ flex: 1, textAlign: 'left', position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center' }}>
            {VALUES.map((value, i) => (
              <ScrollFadeBlock
                key={i}
                style={{
                  ...paragraphStyle,
                  fontSize: 'var(--text-sm)',
                  lineHeight: 'var(--leading-relaxed)',
                  color: COLORS.neutral2,
                  maxWidth: '500px',
                  marginBottom: i < VALUES.length - 1 ? '72px' : 0,
                }}
              >
                <span style={{ color: COLORS.green1, fontSize: 'var(--text-lg)' }}>{value.lead}</span><br />{value.body}
              </ScrollFadeBlock>
            ))}
            <ScrollFadeBlock style={{ marginTop: 96 }}>
              <CtaButton label="Join our team" variant="solid" icon="arrow" iconPosition="right" to="/joinus/" />
            </ScrollFadeBlock>
          </ParallaxText>
        )}

        {isMobile && (
          <motion.img
            src={`${import.meta.env.BASE_URL}images/rune.svg`}
            alt=""
            style={{
              height: '20vh',
              width: 'auto',
              pointerEvents: 'none',
              marginBottom: '48px',
            }}
          />
        )}

        {isMobile && (
        <ParallaxText offset={50} style={{ maxWidth: '900px', textAlign: 'center', position: 'relative', zIndex: 1 }}>
          {VALUES.map((value, i) => (
            <motion.p
              key={i}
              initial={{ opacity: 0.15 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: '-10% 0px' }}
              transition={{ duration: 0.8, delay: i * 0.2, ease: EASE_OUT }}
              style={{
                ...paragraphStyle,
                fontSize: 'var(--text-base)',
                lineHeight: 'var(--leading-normal)',
                color: COLORS.neutral2,
                marginBottom: i < VALUES.length - 1 ? '20px' : 0,
              }}
            >
              <span style={{ color: COLORS.green1 }}>{value.lead}</span>{' '}{value.body}
            </motion.p>
          ))}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.8, ease: EASE_OUT }}
            style={{ marginTop: 32 }}
          >
            <CtaButton label="Join our team" variant="solid" icon="arrow" iconPosition="right" to="/joinus/" />
          </motion.div>
        </ParallaxText>
        )}

      </section>
    </div>
  )
}
